import { useCallback, useMemo } from 'react';
import { useScope } from '@/contexts/ScopeContext';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const DEFAULT_NAMESPACE = 'rugira';

const getLocalStorageSafe = (): StorageLike | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export interface ScopeIdentifiers {
  tenantId?: string | null;
  portfolioId?: string | null;
  modeId?: string | null;
}

const sanitizeFragment = (value: string | null | undefined): string | null =>
  value && value.trim().length > 0 ? value.trim() : null;

export const createScopedStorageKey = (
  baseKey: string,
  scope: ScopeIdentifiers,
  namespace: string = DEFAULT_NAMESPACE,
): string => {
  const resolvedNamespace = namespace.trim().length > 0 ? namespace.trim() : DEFAULT_NAMESPACE;
  const parts = [resolvedNamespace];

  const tenantFragment = sanitizeFragment(scope.tenantId ?? undefined);
  const portfolioFragment = sanitizeFragment(scope.portfolioId ?? undefined);
  const modeFragment = sanitizeFragment(scope.modeId ?? undefined);

  if (tenantFragment) {
    parts.push(`tenant:${tenantFragment}`);
  }
  if (portfolioFragment) {
    parts.push(`portfolio:${portfolioFragment}`);
  }
  if (modeFragment) {
    parts.push(`mode:${modeFragment}`);
  }
  if (!tenantFragment && !portfolioFragment && !modeFragment) {
    parts.push('global');
  }

  parts.push(baseKey.trim());

  return parts.join('|');
};

export interface ScopedStorageReadOptions<T> {
  deserialize?: (value: string) => T;
}

export interface ScopedStorageWriteOptions<T> {
  serialize?: (value: T) => string;
}

export interface UseScopedStorageOptions {
  namespace?: string;
  storage?: StorageLike | null;
}

export interface ScopedStorageHandlers {
  getItem: <T>(key: string, fallback?: T, options?: ScopedStorageReadOptions<T>) => T | undefined;
  setItem: <T>(key: string, value: T | undefined, options?: ScopedStorageWriteOptions<T>) => void;
  removeItem: (key: string) => void;
  buildKey: (key: string) => string;
  isAvailable: boolean;
}

const defaultSerialize = <T,>(value: T): string => JSON.stringify(value);
const defaultDeserialize = <T,>(value: string): T => JSON.parse(value) as T;

export const useScopedStorage = (
  options: UseScopedStorageOptions = {},
): ScopedStorageHandlers => {
  const { namespace = DEFAULT_NAMESPACE, storage: injectedStorage = null } = options;
  const scope = useScope();

  const storage = useMemo<StorageLike | null>(
    () => injectedStorage ?? getLocalStorageSafe(),
    [injectedStorage],
  );

  const tenantId = scope.tenant?.id ?? scope.portfolio?.tenant?.id ?? null;
  const portfolioId = scope.portfolio?.id ?? null;
  const modeId = scope.mode?.id ?? scope.mode?.name ?? null;

  const scopeIdentifiers = useMemo<ScopeIdentifiers>(
    () => ({
      tenantId,
      portfolioId,
      modeId,
    }),
    [tenantId, portfolioId, modeId],
  );

  const buildKey = useCallback(
    (key: string) => createScopedStorageKey(key, scopeIdentifiers, namespace),
    [namespace, scopeIdentifiers],
  );

  const getItem = useCallback(
    <T,>(key: string, fallback?: T, readOptions?: ScopedStorageReadOptions<T>): T | undefined => {
      if (!storage) {
        return fallback;
      }

      const scopedKey = buildKey(key);
      let raw: string | null;

      try {
        raw = storage.getItem(scopedKey);
      } catch {
        return fallback;
      }

      if (raw === null) {
        return fallback;
      }

      const deserialize = readOptions?.deserialize ?? defaultDeserialize<T>;

      try {
        return deserialize(raw);
      } catch {
        return fallback;
      }
    },
    [buildKey, storage],
  );

  const setItem = useCallback(
    <T,>(key: string, value: T | undefined, writeOptions?: ScopedStorageWriteOptions<T>): void => {
      if (!storage) {
        return;
      }

      const scopedKey = buildKey(key);

      if (value === undefined) {
        try {
          storage.removeItem(scopedKey);
        } catch {
          // Intentionally swallow to keep consumer-side logic simple.
        }
        return;
      }

      const serialize = writeOptions?.serialize ?? defaultSerialize<T>;

      try {
        const payload = serialize(value);
        storage.setItem(scopedKey, payload);
      } catch {
        // Ignore write failures (e.g., quota exceeded or blocked storage).
      }
    },
    [buildKey, storage],
  );

  const removeItem = useCallback(
    (key: string): void => {
      if (!storage) {
        return;
      }

      const scopedKey = buildKey(key);

      try {
        storage.removeItem(scopedKey);
      } catch {
        // Ignore removal failures to keep the API forgiving.
      }
    },
    [buildKey, storage],
  );

  return useMemo(
    () => ({
      getItem,
      setItem,
      removeItem,
      buildKey,
      isAvailable: Boolean(storage),
    }),
    [buildKey, getItem, removeItem, setItem, storage],
  );
};
